import React, { useState } from "react";
import axios from "axios";
import { AnimatedCurrency, AnimatedScore, BenchmarkBar, ProductivityImprovementSimulator, ProductivityLeagueTable } from "./GamificationComponents";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Industry averages for "I don't know" defaults
const PRODUCTIVITY_DEFAULTS = {
  absence_days: (headcount) => Math.min(200, Math.round(headcount * 3.9)),
  mental_health_absence_percentage: 28,
  fully_skilled_percentage: 70,
  time_to_competence_months: 6,
  confident_managers_percentage: 50,
  reactive_work_percentage: 40,
  systems_enable_performance: 5
};

function ProductivityCalculator() {
  /*
   * Steps:
   * 0: "Do you measure productivity?" (Yes/No)
   * 1: "How do you measure?" (only if Yes) — auto-skipped if No
   * 2: "Which pressure are you under?"
   * 3: Availability (headcount, absence, mental health)
   * 4: Capability (skilled %, time to competence, managers)
   * 5: Pressure (reactive %, absorb, systems)
   * 6: Results
   */
  const [currentStep, setCurrentStep] = useState(0);
  const [measureProductivity, setMeasureProductivity] = useState(null); // null, true, false

  const [formData, setFormData] = useState({
    current_measure: "",
    confidence_level: 50,
    pressure_type: "",
    headcount: 25,
    absence_days: 20,
    mental_health_absence_percentage: 25,
    fully_skilled_percentage: 70,
    time_to_competence_months: 3,
    confident_managers_percentage: 60,
    reactive_work_percentage: 50,
    can_absorb_more_work: "",
    systems_enable_performance: 5,
    most_fragile_area: ""
  });

  const [useAverage, setUseAverage] = useState({
    absence_days: false,
    mental_health_absence_percentage: false,
    fully_skilled_percentage: false,
    time_to_competence_months: false,
    confident_managers_percentage: false,
    reactive_work_percentage: false,
    systems_enable_performance: false
  });

  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (useAverage[field]) {
      setUseAverage(prev => ({ ...prev, [field]: false }));
    }
  };

  const toggleAverage = (field) => {
    const newVal = !useAverage[field];
    setUseAverage(prev => ({ ...prev, [field]: newVal }));
    if (newVal) {
      const defaultVal = typeof PRODUCTIVITY_DEFAULTS[field] === 'function'
        ? PRODUCTIVITY_DEFAULTS[field](formData.headcount)
        : PRODUCTIVITY_DEFAULTS[field];
      setFormData(prev => ({ ...prev, [field]: defaultVal }));
    }
  };

  // Navigation helpers — step 1 is skipped when measureProductivity is false
  const getNextStep = (current) => {
    if (current === 0 && !measureProductivity) return 2; // skip "how" if No
    return current + 1;
  };
  const getPrevStep = (current) => {
    if (current === 2 && !measureProductivity) return 0; // skip back over "how"
    return current - 1;
  };

  const nextStep = () => setCurrentStep(getNextStep(currentStep));
  const prevStep = () => setCurrentStep(getPrevStep(currentStep));

  const calculateResults = async () => {
    setLoading(true);
    try {
      const response = await axios.post(`${API}/calculate-productivity`, {
        current_measure: formData.current_measure || "not_sure",
        confidence_level: formData.confidence_level,
        pressure_type: formData.pressure_type,
        headcount: formData.headcount,
        absence_days: formData.absence_days,
        mental_health_absence_percentage: formData.mental_health_absence_percentage,
        fully_skilled_percentage: formData.fully_skilled_percentage,
        time_to_competence_months: formData.time_to_competence_months,
        confident_managers_percentage: formData.confident_managers_percentage,
        reactive_work_percentage: formData.reactive_work_percentage,
        can_absorb_more_work: formData.can_absorb_more_work || "not_sure",
        systems_enable_performance: formData.systems_enable_performance,
        most_fragile_area: formData.most_fragile_area
      });
      setResults(response.data);
      setCurrentStep(6);
    } catch (error) {
      console.error('Error calculating productivity:', error);
      alert('Error calculating results. Please try again.');
    }
    setLoading(false);
  };

  const formatCurrency = (amount) =>
    new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP', maximumFractionDigits: 0 }).format(amount);

  // Reusable "I don't know" toggle
  const DontKnowToggle = ({ field }) => (
    <button
      onClick={() => toggleAverage(field)}
      data-testid={`dont-know-${field}`}
      className={`mt-3 text-sm px-4 py-2 rounded-full border transition-all ${
        useAverage[field]
          ? 'bg-amber-50 border-amber-300 text-amber-700 font-medium'
          : 'bg-white border-slate-200 text-slate-500 hover:border-amber-300 hover:text-amber-600'
      }`}
    >
      {useAverage[field] ? 'Using industry average' : "I don't know — use industry average"}
    </button>
  );

  // Progress bar — maps visible steps
  const ProgressBar = () => {
    const visibleSteps = measureProductivity === false ? 5 : 6; // total form steps (excluding results)
    let displayStep = currentStep;
    if (!measureProductivity && currentStep >= 2) displayStep = currentStep - 1; // adjust for skipped step
    const stepsRemaining = Math.max(0, visibleSteps - displayStep - 1);
    return (
      <div className="mb-8">
        <div className="flex justify-between items-center mb-2">
          <span className="text-sm font-medium text-slate-600">Step {displayStep + 1} of {visibleSteps}</span>
          {stepsRemaining > 0 && (
            <span className="text-sm text-green-600 font-medium">
              {stepsRemaining === 1 ? 'Last step!' : `${stepsRemaining} steps remaining`}
            </span>
          )}
        </div>
        <div className="w-full bg-slate-200 rounded-full h-3">
          <div className="h-3 rounded-full transition-all duration-500 ease-out" style={{ width: `${(displayStep / (visibleSteps - 1)) * 100}%`, background: 'linear-gradient(90deg, #facf39 0%, #f9c307 100%)' }}></div>
        </div>
      </div>
    );
  };

  // Slider input component
  const SliderInput = ({ field, label, min, max, step, suffix, disabled }) => (
    <div className="text-center">
      <label className="block text-sm font-medium mb-2 text-slate-700">{label}</label>
      <div className="text-4xl font-bold mb-4" style={{ color: useAverage[field] ? '#d97706' : '#333333' }}>
        {formData[field]}{suffix || ''}
      </div>
      <input
        type="range" min={min} max={max} step={step || 1}
        value={formData[field]}
        onChange={(e) => handleInputChange(field, parseInt(e.target.value))}
        className="w-full slider mb-2"
        disabled={disabled || useAverage[field]}
        style={{ opacity: useAverage[field] ? 0.5 : 1 }}
      />
      <div className="flex justify-between text-xs text-slate-500">
        <span>{min}{suffix || ''}</span><span>{max}{suffix ? '+' : ''}{suffix || ''}</span>
      </div>
      {PRODUCTIVITY_DEFAULTS[field] !== undefined && <DontKnowToggle field={field} />}
    </div>
  );

  // --- STEP 0: Do you measure productivity? ---
  if (currentStep === 0) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center px-4">
        <div className="max-w-2xl mx-auto w-full">
          <div className="bg-white rounded-2xl shadow-xl p-8 md:p-12">
            <h2 className="text-3xl font-bold mb-8 text-center" style={{color: '#333333'}} data-testid="measure-heading">
              Do you currently measure productivity?
            </h2>
            <div className="flex justify-center space-x-6 mb-8">
              <button
                data-testid="measure-yes"
                onClick={() => {
                  setMeasureProductivity(true);
                  setCurrentStep(1);
                }}
                className={`px-10 py-6 rounded-xl border-2 transition-all text-center min-w-[140px] ${
                  measureProductivity === true ? 'border-green-500 bg-green-50' : 'border-slate-200 hover:border-green-300'
                }`}
              >
                <div className="text-3xl mb-2">✓</div>
                <div className="font-semibold text-lg">Yes</div>
              </button>
              <button
                data-testid="measure-no"
                onClick={() => {
                  setMeasureProductivity(false);
                  setFormData(prev => ({ ...prev, current_measure: 'not_sure', confidence_level: 0 }));
                  setCurrentStep(2); // skip to pressure step
                }}
                className={`px-10 py-6 rounded-xl border-2 transition-all text-center min-w-[140px] ${
                  measureProductivity === false ? 'border-red-500 bg-red-50' : 'border-slate-200 hover:border-red-300'
                }`}
              >
                <div className="text-3xl mb-2">✗</div>
                <div className="font-semibold text-lg">No</div>
              </button>
            </div>
            <p className="text-center text-sm text-slate-500">
              No wrong answer — most organisations find it harder than expected to define productivity clearly.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // --- STEP 1: How do you measure? (only if Yes) ---
  if (currentStep === 1) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 px-4 py-8">
        <div className="max-w-3xl mx-auto">
          <ProgressBar />
          <div className="bg-white rounded-2xl shadow-xl p-8 md:p-12">
            <h2 className="text-3xl font-bold mb-6 text-center" style={{color: '#333333'}}>
              How do you measure productivity?
            </h2>
            <div className="grid grid-cols-1 gap-3 mb-8">
              {[
                { value: "output_per_hour", label: "Output per hour" },
                { value: "revenue_per_head", label: "Revenue per employee" },
                { value: "machine_utilisation", label: "Machine/equipment utilisation" },
                { value: "project_deadlines", label: "Project deadlines met" },
                { value: "other", label: "Other / Combination" }
              ].map((option) => (
                <button
                  key={option.value}
                  onClick={() => handleInputChange('current_measure', option.value)}
                  data-testid={`measure-${option.value}`}
                  className={`p-4 rounded-lg border-2 text-left transition-all ${
                    formData.current_measure === option.value
                      ? 'border-green-500 bg-green-50 text-green-700'
                      : 'border-slate-200 hover:border-green-300'
                  }`}
                >
                  <span className="font-medium">{option.label}</span>
                </button>
              ))}
            </div>

            <div className="mb-8">
              <h3 className="text-lg font-semibold mb-4" style={{color: '#333333'}}>
                How confident are you that this reflects overall productivity?
              </h3>
              <div className="text-center mb-4">
                <div className="text-4xl font-bold" style={{ color: '#333333' }}>{formData.confidence_level}%</div>
              </div>
              <input type="range" min="0" max="100" value={formData.confidence_level} onChange={(e) => handleInputChange('confidence_level', parseInt(e.target.value))} className="w-full slider mb-2" />
              <div className="flex justify-between text-sm text-slate-500"><span>Not confident</span><span>Very confident</span></div>
            </div>

            <div className="flex justify-between">
              <button onClick={prevStep} className="bg-slate-200 text-slate-800 px-6 py-3 rounded-lg hover:bg-slate-300 transition-colors">Back</button>
              <button onClick={nextStep} disabled={!formData.current_measure} className="text-white px-8 py-4 rounded-lg text-lg font-medium hover:opacity-90 transition-all disabled:opacity-50 disabled:cursor-not-allowed" style={{ background: '#333333' }}>Continue</button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // --- STEP 2: Which pressure are you under? ---
  if (currentStep === 2) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 px-4 py-8">
        <div className="max-w-3xl mx-auto">
          <ProgressBar />
          <div className="bg-white rounded-2xl shadow-xl p-8 md:p-12">
            <h2 className="text-3xl font-bold mb-8 text-center" style={{color: '#333333'}}>
              Which pressure are you under right now?
            </h2>
            <div className="grid grid-cols-1 gap-4 mb-8">
              {[
                { value: "increase_output", label: "Increase output with the same headcount" },
                { value: "cut_costs", label: "Cut costs without hurting performance" },
                { value: "deliver_more", label: "Deliver more with fewer people" },
                { value: "reduce_temp_staff", label: "Reduce reliance on temporary staff" }
              ].map((option) => (
                <button
                  key={option.value}
                  onClick={() => handleInputChange('pressure_type', option.value)}
                  data-testid={`pressure-${option.value}`}
                  className={`p-5 rounded-lg border-2 text-left transition-all ${
                    formData.pressure_type === option.value
                      ? 'border-green-500 bg-green-50 text-green-700'
                      : 'border-slate-200 hover:border-green-300'
                  }`}
                >
                  <span className="font-medium text-lg">{option.label}</span>
                </button>
              ))}
            </div>
            <div className="flex justify-between">
              <button onClick={prevStep} className="bg-slate-200 text-slate-800 px-6 py-3 rounded-lg hover:bg-slate-300 transition-colors">Back</button>
              <button onClick={nextStep} disabled={!formData.pressure_type} className="text-white px-8 py-4 rounded-lg text-lg font-medium hover:opacity-90 transition-all disabled:opacity-50 disabled:cursor-not-allowed" style={{ background: '#333333' }}>Continue</button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // --- STEP 3: Availability ---
  if (currentStep === 3) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 px-4 py-8">
        <div className="max-w-4xl mx-auto">
          <ProgressBar />
          <div className="bg-white rounded-2xl shadow-xl p-8 md:p-12">
            <div className="flex items-center justify-center mb-2">
              <span className="text-sm font-medium text-orange-600 bg-orange-50 px-3 py-1 rounded-full">Availability</span>
            </div>
            <h2 className="text-3xl font-bold mb-2 text-center" style={{color: '#333333'}}>
              How available is your team?
            </h2>
            <p className="text-slate-500 text-center mb-10">People can't be productive if they're not there.</p>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              <div className="text-center">
                <label className="block text-sm font-medium mb-2 text-slate-700">Team headcount</label>
                <div className="text-4xl font-bold mb-4" style={{ color: '#333333' }}>{formData.headcount}</div>
                <input type="range" min="5" max="200" value={formData.headcount} onChange={(e) => handleInputChange('headcount', parseInt(e.target.value))} className="w-full slider mb-2" />
                <div className="flex justify-between text-xs text-slate-500"><span>5</span><span>200+</span></div>
              </div>
              <SliderInput field="absence_days" label="Total absence days (last 6 months)" min={0} max={200} suffix="" />
              <SliderInput field="mental_health_absence_percentage" label="% due to mental health / stress" min={0} max={50} suffix="%" />
            </div>

            <div className="flex justify-between mt-10">
              <button onClick={prevStep} className="bg-slate-200 text-slate-800 px-6 py-3 rounded-lg hover:bg-slate-300 transition-colors">Back</button>
              <button onClick={nextStep} className="text-white px-8 py-4 rounded-lg text-lg font-medium hover:opacity-90 transition-all" style={{ background: '#333333' }}>Continue</button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // --- STEP 4: Capability ---
  if (currentStep === 4) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 px-4 py-8">
        <div className="max-w-4xl mx-auto">
          <ProgressBar />
          <div className="bg-white rounded-2xl shadow-xl p-8 md:p-12">
            <div className="flex items-center justify-center mb-2">
              <span className="text-sm font-medium text-blue-600 bg-blue-50 px-3 py-1 rounded-full">Capability</span>
            </div>
            <h2 className="text-3xl font-bold mb-2 text-center" style={{color: '#333333'}}>
              How capable is your team?
            </h2>
            <p className="text-slate-500 text-center mb-10">Skills gaps and management confidence directly affect output.</p>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              <SliderInput field="fully_skilled_percentage" label="% you'd call 'fully skilled'" min={0} max={100} suffix="%" />
              <SliderInput field="time_to_competence_months" label="Months for new starter to reach full output" min={1} max={24} suffix="" />
              <SliderInput field="confident_managers_percentage" label="% of managers confident managing underperformance" min={0} max={100} suffix="%" />
            </div>

            <div className="flex justify-between mt-10">
              <button onClick={prevStep} className="bg-slate-200 text-slate-800 px-6 py-3 rounded-lg hover:bg-slate-300 transition-colors">Back</button>
              <button onClick={nextStep} className="text-white px-8 py-4 rounded-lg text-lg font-medium hover:opacity-90 transition-all" style={{ background: '#333333' }}>Continue</button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // --- STEP 5: Pressure ---
  if (currentStep === 5) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 px-4 py-8">
        <div className="max-w-4xl mx-auto">
          <ProgressBar />
          <div className="bg-white rounded-2xl shadow-xl p-8 md:p-12">
            <div className="flex items-center justify-center mb-2">
              <span className="text-sm font-medium text-red-600 bg-red-50 px-3 py-1 rounded-full">Pressure</span>
            </div>
            <h2 className="text-3xl font-bold mb-2 text-center" style={{color: '#333333'}}>
              How much pressure is your team under?
            </h2>
            <p className="text-slate-500 text-center mb-10">Overloaded teams look busy but deliver less.</p>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
              <SliderInput field="reactive_work_percentage" label="% of time staff work reactively" min={0} max={100} suffix="%" />
              <SliderInput field="systems_enable_performance" label="How well do systems enable performance? (1-10)" min={1} max={10} suffix="/10" />
            </div>

            <div className="text-center mb-4">
              <label className="block text-sm font-medium mb-4 text-slate-700">Can your team absorb more work without burning out?</label>
              <div className="flex justify-center space-x-4">
                {[
                  { value: "yes", label: "Yes", activeClass: "border-green-500 bg-green-50 text-green-700" },
                  { value: "not_sure", label: "Not sure", activeClass: "border-amber-500 bg-amber-50 text-amber-700" },
                  { value: "no", label: "No", activeClass: "border-red-500 bg-red-50 text-red-700" }
                ].map((option) => (
                  <button
                    key={option.value}
                    onClick={() => handleInputChange('can_absorb_more_work', option.value)}
                    data-testid={`absorb-${option.value}`}
                    className={`px-6 py-3 rounded-lg border-2 transition-all font-medium ${
                      formData.can_absorb_more_work === option.value ? option.activeClass : 'border-slate-200 hover:border-slate-300'
                    }`}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="flex justify-between mt-10">
              <button onClick={prevStep} className="bg-slate-200 text-slate-800 px-6 py-3 rounded-lg hover:bg-slate-300 transition-colors">Back</button>
              <button onClick={calculateResults} disabled={loading} data-testid="prod-calculate-btn" className="text-white px-8 py-4 rounded-lg text-lg font-medium hover:opacity-90 transition-all disabled:opacity-50 disabled:cursor-not-allowed" style={{ background: '#333333' }}>
                {loading ? 'Analysing...' : 'Get My Results'}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // --- STEP 6: Results ---
  if (currentStep === 6 && results) {
    // Benchmark averages for productivity metrics
    const avgAbsenceDaysPerHead = 3.9;
    const avgMentalHealth = 28;
    const avgFullySkilled = 70;
    const avgTimeToCompetence = 6;
    const avgConfidentManagers = 50;
    const avgReactiveWork = 40;
    const absencePerHead = formData.headcount > 0 ? (formData.absence_days / formData.headcount * 2).toFixed(1) : 0; // annualised
    const avgAbsenceAnnual = (avgAbsenceDaysPerHead * 2).toFixed(1);

    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 py-12">
        <div className="max-w-4xl mx-auto px-4">
          <div className="bg-white rounded-2xl shadow-xl p-8 md:p-12">
            <div className="text-center mb-8">
              <h1 className="text-4xl font-bold text-slate-800 mb-4">Your Productivity Diagnostic Results</h1>
              <div className={`text-6xl font-bold mb-3 ${
                results.fragility_score >= 76 ? 'text-red-600' :
                results.fragility_score >= 56 ? 'text-orange-500' :
                results.fragility_score >= 31 ? 'text-amber-500' : 'text-green-600'
              }`} data-testid="fragility-score">
                <AnimatedScore score={results.fragility_score} />
              </div>
              <div className={`inline-block text-lg font-semibold px-4 py-1 rounded-full mb-4 ${
                results.fragility_score >= 76 ? 'bg-red-100 text-red-700' :
                results.fragility_score >= 56 ? 'bg-orange-100 text-orange-700' :
                results.fragility_score >= 31 ? 'bg-amber-100 text-amber-700' : 'bg-green-100 text-green-700'
              }`}>
                {results.fragility_score >= 76 ? 'Critical Risk' :
                 results.fragility_score >= 56 ? 'High Risk' :
                 results.fragility_score >= 31 ? 'Moderate Risk' : 'Low Risk'}
              </div>
              <p className="text-slate-600 text-base max-w-2xl mx-auto">
                {results.fragility_score >= 76
                  ? "Your team's productivity is critically exposed. Multiple people-related risks are compounding and likely already affecting output, costs, and morale."
                  : results.fragility_score >= 56
                  ? "Your team has significant productivity risks. Without intervention, these are likely to worsen and create visible performance or cost problems within the next 6-12 months."
                  : results.fragility_score >= 31
                  ? "Your team has moderate exposure. Some risks are present but manageable — targeted action now can prevent them from escalating."
                  : "Your team's productivity foundations are relatively stable. Focus on maintaining what's working and addressing any specific weak spots."}
              </p>
            </div>

            {/* Score scale */}
            <div className="mb-10 px-4">
              <div className="relative h-4 rounded-full overflow-hidden bg-gradient-to-r from-green-400 via-amber-400 via-orange-400 to-red-500">
                <div className="absolute top-0 h-full w-1 bg-white border-2 border-slate-800 rounded-full -translate-x-1/2 transition-all duration-1000"
                  style={{ left: `${results.fragility_score}%` }}></div>
              </div>
              <div className="flex justify-between text-xs text-slate-500 mt-2 px-1">
                <span>0 — Low Risk</span>
                <span>30 — Moderate</span>
                <span>55 — High</span>
                <span>75 — Critical — 100</span>
              </div>
            </div>

            {/* What the score means */}
            <div className="bg-slate-50 border border-slate-200 rounded-xl p-6 mb-8" data-testid="score-explanation">
              <h3 className="text-lg font-bold text-slate-800 mb-3">What does this score mean?</h3>
              <p className="text-sm text-slate-700 mb-3">
                Your Fragility Score measures <strong>how exposed your team's productivity is to people-related risks</strong>. It's not a performance score — it's a risk indicator. A score of {results.fragility_score} means {results.fragility_score}% of maximum risk exposure.
              </p>
              <p className="text-sm text-slate-700 mb-4">It's calculated from three equally weighted areas:</p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
                <div className="bg-white rounded-lg p-3 border border-slate-200">
                  <div className="font-semibold text-orange-700 mb-1">Availability ({results.risk_breakdown.availability}/100)</div>
                  <p className="text-slate-600 text-xs">Absence rates, mental health impact, and team size vulnerability. Higher absence and smaller teams increase risk.</p>
                </div>
                <div className="bg-white rounded-lg p-3 border border-slate-200">
                  <div className="font-semibold text-blue-700 mb-1">Capability ({results.risk_breakdown.capability}/100)</div>
                  <p className="text-slate-600 text-xs">Skills gaps, time for new starters to reach full output, and management confidence. Fewer skilled staff = higher risk.</p>
                </div>
                <div className="bg-white rounded-lg p-3 border border-slate-200">
                  <div className="font-semibold text-red-700 mb-1">Pressure ({results.risk_breakdown.pressure}/100)</div>
                  <p className="text-slate-600 text-xs">Reactive vs proactive work balance, capacity to absorb more, and how well systems support performance.</p>
                </div>
              </div>
            </div>

            {/* Animated risk breakdown */}
            <div className="grid md:grid-cols-3 gap-6 mb-8">
              <div className="bg-orange-50 border border-orange-200 rounded-xl p-6">
                <h4 className="font-medium text-orange-900 mb-2">Availability</h4>
                <div className="text-2xl font-bold text-orange-700 mb-2"><AnimatedScore score={results.risk_breakdown.availability} /></div>
                <p className="text-sm text-orange-800">Headcount, absence, mental health</p>
              </div>
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-6">
                <h4 className="font-medium text-blue-900 mb-2">Capability</h4>
                <div className="text-2xl font-bold text-blue-700 mb-2"><AnimatedScore score={results.risk_breakdown.capability} /></div>
                <p className="text-sm text-blue-800">Skills, training, management</p>
              </div>
              <div className="bg-red-50 border border-red-200 rounded-xl p-6">
                <h4 className="font-medium text-red-900 mb-2">Pressure</h4>
                <div className="text-2xl font-bold text-red-700 mb-2"><AnimatedScore score={results.risk_breakdown.pressure} /></div>
                <p className="text-sm text-red-800">Workload, systems, capacity</p>
              </div>
            </div>

            {/* Animated cost breakdown */}
            <div className="bg-slate-50 border border-slate-200 rounded-xl p-8 mb-8">
              <h3 className="text-xl font-medium text-slate-800 mb-6 text-center">
                Estimated Annual Productivity Drag: <AnimatedCurrency amount={results.estimated_productivity_drag} className="text-blue-600" />
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                <div className="text-center">
                  <div className="font-bold text-lg text-slate-700"><AnimatedCurrency amount={results.cost_breakdown.absence_cost} /></div>
                  <div className="text-slate-600">Absence</div>
                </div>
                <div className="text-center">
                  <div className="font-bold text-lg text-slate-700"><AnimatedCurrency amount={results.cost_breakdown.turnover_cost} /></div>
                  <div className="text-slate-600">Turnover</div>
                </div>
                <div className="text-center">
                  <div className="font-bold text-lg text-slate-700"><AnimatedCurrency amount={results.cost_breakdown.competence_delay_cost} /></div>
                  <div className="text-slate-600">Training delays</div>
                </div>
                <div className="text-center">
                  <div className="font-bold text-lg text-slate-700"><AnimatedCurrency amount={results.cost_breakdown.underperformance_cost} /></div>
                  <div className="text-slate-600">Underperformance</div>
                </div>
              </div>
            </div>

            {/* Benchmark comparison */}
            <div className="bg-slate-50 border border-slate-200 rounded-xl p-6 mb-8" data-testid="prod-benchmark-comparison">
              <h3 className="text-lg font-bold text-slate-800 mb-4">How You Compare to Industry Averages</h3>
              <BenchmarkBar label="Absence days per employee (annual)" yourValue={parseFloat(absencePerHead)} avgValue={parseFloat(avgAbsenceAnnual)} lowerIsBetter={true} />
              <BenchmarkBar label="Mental health absence" yourValue={formData.mental_health_absence_percentage} avgValue={avgMentalHealth} unit="%" lowerIsBetter={true} />
              <BenchmarkBar label="Fully skilled staff" yourValue={formData.fully_skilled_percentage} avgValue={avgFullySkilled} unit="%" lowerIsBetter={false} />
              <BenchmarkBar label="Time to competence" yourValue={formData.time_to_competence_months} avgValue={avgTimeToCompetence} unit=" months" lowerIsBetter={true} />
              <BenchmarkBar label="Confident managers" yourValue={formData.confident_managers_percentage} avgValue={avgConfidentManagers} unit="%" lowerIsBetter={false} />
              <BenchmarkBar label="Reactive work" yourValue={formData.reactive_work_percentage} avgValue={avgReactiveWork} unit="%" lowerIsBetter={true} />
            </div>

            {/* League table */}
            <ProductivityLeagueTable fragilityScore={results.fragility_score} />

            {/* Improvement simulator */}
            <ProductivityImprovementSimulator results={results} formData={formData} />

            <div className="bg-green-50 border border-green-200 rounded-xl p-8 mb-8">
              <h3 className="text-xl font-medium text-green-900 mb-6">Recommended Actions</h3>
              <ul className="space-y-3">
                {results.recommendations.map((rec, index) => (
                  <li key={index} className="flex items-start text-green-800">
                    <span className="flex-shrink-0 w-6 h-6 bg-green-200 rounded-full flex items-center justify-center text-green-700 text-sm font-bold mr-3 mt-0.5">{index + 1}</span>
                    <span>{rec}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="bg-gradient-to-br from-blue-50 to-green-50 border border-blue-200 rounded-2xl p-8 mt-8">
              <div className="text-center mb-6">
                <h3 className="text-3xl font-bold text-slate-800 mb-4">Turn Insight Into Action</h3>
                <p className="text-slate-700 max-w-2xl mx-auto mb-2">Without leadership buy-in, culture change rarely sticks. The next step is a focused leadership conversation.</p>
                <p className="font-semibold text-slate-800">Book a free 30-minute Culture Pulse Check.</p>
              </div>
              <div className="bg-white rounded-lg p-6 mb-6 max-w-2xl mx-auto">
                <p className="font-medium text-slate-800 mb-3">We'll:</p>
                <ul className="space-y-2 text-slate-700">
                  {["Review your calculator results", "Identify your biggest culture or productivity pressure points", "Clarify where leadership alignment may be holding progress back", "Outline practical next steps that deliver value to decision-makers"].map((item, i) => (
                    <li key={i} className="flex items-start"><span className="flex-shrink-0 w-6 h-6 bg-green-200 rounded-full flex items-center justify-center text-green-700 text-sm font-bold mr-3 mt-0.5">✓</span><span>{item}</span></li>
                  ))}
                </ul>
              </div>
              <div className="text-center mb-6"><p className="text-slate-600 italic">No obligation. No generic advice. Just a clear conversation about what's realistic and commercially sensible.</p></div>
              <div className="text-center">
                <a href="https://calendly.com/ollie-kalobrate/kalobrate-introductions" target="_blank" rel="noopener noreferrer" data-testid="prod-cta-button" className="text-white px-10 py-4 rounded-lg text-xl font-bold hover:opacity-90 transition-colors inline-block text-center shadow-lg" style={{backgroundColor: '#333333'}}>Book My Free Culture Pulse Check</a>
              </div>
            </div>

            <div className="text-xs text-slate-500 bg-slate-50 p-6 rounded-lg mt-8">
              <p><strong>Methodology:</strong> Analysis based on UK industry averages for productivity costs, absence rates, and performance factors. Individual results may vary based on organisation size, sector, and specific circumstances.</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return null;
}

export default ProductivityCalculator;
